import React, { useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import axios from "axios";

const InquiryModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
  name: "",
  email: "",
  number: "",
  projectType: "",
  query: "",
  method: "email",
});


  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState("");

  const projectTypes = [
    "Residential Rooftop Solar",
    "Commercial Solar Installation",
    "Telecom Electrification",
    "HT/LT Work",
    "Industrial Solutions",
    "Other",
  ];

  const budgetRanges = [
    "Under ₹5 Lakhs",
    "₹5 - 10 Lakhs",
    "₹10 - 25 Lakhs",
    "₹25 - 50 Lakhs",
    "₹50 Lakhs - 1 Crore",
    "Above ₹1 Crore",
  ];

  // Validation rules
 const validateField = (name, value) => {
  switch (name) {
    case "name":
      return value.trim().length < 2
        ? "Name must be at least 2 characters"
        : "";

    case "email":
      return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? "Invalid email address"
        : "";

    case "number":
      return !/^[0-9]{10}$/.test(value.replace(/\s/g, ""))
        ? "Phone must be 10 digits"
        : "";

    case "query":
      return value.trim().length < 10
        ? "Message must be at least 10 characters"
        : "";

    default:
      return "";
  }
};


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Real-time validation
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();

  const newErrors = {};

  ["name", "email", "number", "query"].forEach((key) => {
    const error = validateField(key, formData[key]);
    if (error) newErrors[key] = error;
  });

  if (!formData.projectType) {
    newErrors.projectType = "Please select a project type";
  }

  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    toast.error("Please fix the errors before submitting");
    return;
  }

  setIsSubmitting(true);

  try {
    const payload = {
      name: formData.name,
      email: formData.email,
      number: formData.number,
      projectType: formData.projectType,
      query: formData.query,
      method: formData.method,
    };

    await axios.post(
      `${process.env.REACT_APP_API_URL}/enquiry/create`,
      payload
    );

    toast.success("Inquiry submitted successfully!");

    setFormData({
      name: "",
      email: "",
      number: "",
      projectType: "",
      query: "",
      method: "email",
    });

    setErrors({});
  } catch (error) {
    console.error(error);
    toast.error("Failed to submit inquiry.");
  } finally {
    setIsSubmitting(false);
    onClose();
  }
};


  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm ">
      <div className="relative w-full max-w-3xl mx-4 bg-white rounded-2xl shadow-2xl overflow-y-auto max-h-[90vh] no-scrollbar">

         <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black text-xl"
        >
          ✕
        </button>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-4"
        >
          <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold ">
            GET IN TOUCH
          </span>
        </motion.div>

        <div className="">

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10 border border-gray-100">
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className={`w-5 h-5 ${focusedField === 'name' ? 'text-blue-600' : 'text-gray-400'} transition-colors duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField('')}
                      placeholder="Enter your full name"
                      className={`w-full pl-12 pr-4 py-3 sm:py-4 rounded-xl border-2 ${
                        errors.name
                          ? "border-red-500 focus:ring-red-500"
                          : focusedField === 'name'
                          ? "border-blue-500 focus:ring-blue-500"
                          : "border-gray-200 focus:ring-blue-500"
                      } focus:ring-2 focus:border-transparent transition-all duration-200 text-sm sm:text-base`}
                    />
                  </div>
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Email & Phone */}
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className={`w-5 h-5 ${focusedField === 'email' ? 'text-blue-600' : 'text-gray-400'} transition-colors duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField('')}
                        placeholder="you@example.com"
                        className={`w-full pl-12 pr-4 py-3 sm:py-4 rounded-xl border-2 ${
                          errors.email
                            ? "border-red-500 focus:ring-red-500"
                            : focusedField === 'email'
                            ? "border-blue-500 focus:ring-blue-500"
                            : "border-gray-200 focus:ring-blue-500"
                        } focus:ring-2 focus:border-transparent transition-all duration-200 text-sm sm:text-base`}
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className={`w-5 h-5 ${focusedField === 'phone' ? 'text-blue-600' : 'text-gray-400'} transition-colors duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <input
                        type="tel"
                        name="number"
                        value={formData.number}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('phone')}
                        onBlur={() => setFocusedField('')}
                        placeholder="9876543210"
                        className={`w-full pl-12 pr-4 py-3 sm:py-4 rounded-xl border-2 ${
                          errors.phone
                            ? "border-red-500 focus:ring-red-500"
                            : focusedField === 'phone'
                            ? "border-blue-500 focus:ring-blue-500"
                            : "border-gray-200 focus:ring-blue-500"
                        } focus:ring-2 focus:border-transparent transition-all duration-200 text-sm sm:text-base`}
                      />
                    </div>
                    {errors.number && (
                      <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.number}
                      </p>
                    )}
                  </div>
                </div>

                {/* Project Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className={`w-5 h-5 ${focusedField === 'projectType' ? 'text-blue-600' : 'text-gray-400'} transition-colors duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('projectType')}
                      onBlur={() => setFocusedField('')}
                      className={`w-full pl-12 pr-4 py-3 sm:py-4 rounded-xl border-2 ${
                        errors.projectType
                          ? "border-red-500 focus:ring-red-500"
                          : focusedField === 'projectType'
                          ? "border-blue-500 focus:ring-blue-500"
                          : "border-gray-200 focus:ring-blue-500"
                      } focus:ring-2 focus:border-transparent transition-all duration-200 text-sm sm:text-base appearance-none bg-white cursor-pointer`}
                    >
                      <option value="">Select project type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  {errors.projectType && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.projectType}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Details <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <textarea
                      name="query"
                      value={formData.query}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField('')}
                      rows={5}
                      placeholder="Tell us about your project requirements, location, timeline, etc."
                      className={`w-full px-4 py-3 sm:py-4 rounded-xl border-2 ${
                        errors.message
                          ? "border-red-500 focus:ring-red-500"
                          : focusedField === 'message'
                          ? "border-blue-500 focus:ring-blue-500"
                          : "border-gray-200 focus:ring-blue-500"
                      } focus:ring-2 focus:border-transparent transition-all duration-200 resize-none text-sm sm:text-base`}
                    />
                    <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                      {formData.query.length} / 500
                    </div>
                  </div>
                  {errors.query && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.query}
                    </p>
                  )}
                </div>

                {/* Preferred Contact Method */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Preferred Contact Method
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { value: "email", label: "Email", icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
                      { value: "phone", label: "Phone", icon: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" },
                      { value: "whatsapp", label: "WhatsApp", icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" },
                    ].map((method) => (
                      <label
                        key={method.value}
                        className={`flex-1 min-w-[140px] flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                          formData.method === method.value
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-green-200 hover:bg-green-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="method"
                          value={method.value}
                          checked={formData.method === method.value}
                          onChange={handleChange}
                          className="hidden"
                        />
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          formData.method === method.value
                            ? "bg-green-600 text-white"
                            : "bg-gray-100 text-gray-400"
                        }`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={method.icon} />
                          </svg>
                        </div>
                        <span className={`font-medium text-sm ${
                          formData.method === method.value
                            ? "text-green-600"
                            : "text-gray-700"
                        }`}>
                          {method.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  className={`w-full py-4 sm:py-5 rounded-xl font-bold text-base sm:text-lg text-white shadow-lg transition-all duration-200 ${
                    isSubmitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-green-500 via-lime-500 to-emerald-500 hover:from-green-600 hover:via-lime-600 hover:to-emerald-600 hover:shadow-xl"
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Send Inquiry
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </span>
                  )}
                </motion.button>

                {/* Privacy Note */}
                <p className="text-xs text-center text-gray-500">
                  🔒 Your information is secure and will never be shared with third parties.
                  By submitting, you agree to our{" "}
                  <a href="/privacy" className="text-blue-600 hover:underline">
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default InquiryModal;